import { Fragment } from 'react'
import type { CSSProperties } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Tooltip } from '@base-ui/react/tooltip'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { apiFetch } from '@/api/client'
import type { CloudEnvironment, ComplianceSummary } from '@/domain'
import { isNavActive, NAV_SECTIONS } from './nav-config'
import type { NavBadge, NavItem, NavSubItem } from './nav-config'
import { TOOLTIP_STYLE } from './popup-style'

/**
 * Grouped sidenav (REWRITE-PLAN §4). Collapses to a 64px icon rail with
 * tooltips; active item gets a sienna tint bar.
 *
 * Badges are severity-tiered so the rail reads as a 10-second triage: routine
 * workload (pending access requests) uses a NEUTRAL token; risk signals (open
 * compliance violations, environments in error) use the brand blue. Brand
 * orange is reserved for identity/selection so "work waiting" never looks like
 * branding. All badge queries degrade silently (count → null) on error.
 */

export const RAIL_WIDTH = 248
export const RAIL_WIDTH_COLLAPSED = 64

/** Risk badges turn the collapsed-rail dot blue; workload badges stay neutral. */
const BADGE_IS_RISK: Record<NavBadge, boolean> = {
  'pending-sars': false,
  violations: true,
  'env-errors': true,
}

const BADGE_NOUN: Record<NavBadge, string> = {
  'pending-sars': 'pending',
  violations: 'open findings',
  'env-errors': 'in error',
}

interface BadgeData {
  badge: NavBadge
  count: number
  risk: boolean
}

/**
 * Count from a list/page/total response. Prefers a `total` field (so a badge
 * doesn't freeze at page size once the list pages) and only falls back to the
 * materialized array length when no total is present.
 */
function countItems(data: unknown): number | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const rec = data as Record<string, unknown>
    if (typeof rec.total === 'number') return rec.total
    if (Array.isArray(rec.items)) return rec.items.length
  }
  if (Array.isArray(data)) return data.length
  return null
}

function usePendingSarCount(): number | null {
  // limit=1 so the badge costs one cheap count query — we read `total`, not the
  // (one-row) page.
  const { data, isError } = useQuery({
    queryKey: ['sars', { status: 'pending', count: true }],
    queryFn: () =>
      apiFetch<unknown>({ url: '/api/v1/sars', params: { status: 'pending', limit: 1 } }),
    retry: false,
    staleTime: 60_000,
  })
  if (isError) return null
  return countItems(data)
}

/** Open compliance violations — the single most board-reportable governance #. */
function useOpenViolationsCount(): number | null {
  const { data, isError } = useQuery({
    queryKey: ['compliance', 'summary'],
    queryFn: () => apiFetch<ComplianceSummary>({ url: '/api/v1/compliance/summary' }),
    retry: false,
    staleTime: 60_000,
  })
  if (isError || !data) return null
  return data.violationsOpen
}

/** Environments in `status='error'` — "are we blind anywhere" signal. */
function useEnvErrorCount(): number | null {
  const { data, isError } = useQuery({
    queryKey: ['envs'],
    queryFn: () => apiFetch<CloudEnvironment[]>({ url: '/api/v1/envs' }),
    retry: false,
    staleTime: 60_000,
  })
  if (isError || !Array.isArray(data)) return null
  return data.filter((e) => e.backendStatus === 'error').length
}

/** All live badge counts in one place; consumed per nav item by `badge`. */
function useBadgeCounts(): Record<NavBadge, number | null> {
  return {
    'pending-sars': usePendingSarCount(),
    violations: useOpenViolationsCount(),
    'env-errors': useEnvErrorCount(),
  }
}

/** Visible (count > 0) badges for an item, in declared order. */
function visibleBadges(
  item: NavItem,
  counts: Record<NavBadge, number | null>,
): BadgeData[] {
  return (item.badges ?? []).flatMap((badge) => {
    const count = counts[badge]
    if (count === null || count <= 0) return []
    return [{ badge, count, risk: BADGE_IS_RISK[badge] }]
  })
}

function Badge({ data }: { data: BadgeData }) {
  return (
    <span
      aria-label={`${data.count} ${BADGE_NOUN[data.badge]}`}
      style={{
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 'var(--r-pill)',
        background: data.risk ? 'var(--info-500)' : 'var(--bg-sunk)',
        color: data.risk ? 'var(--brand-50)' : 'var(--text-soft)',
        border: data.risk ? 0 : '1px solid var(--border)',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'var(--ff-mono)',
        fontVariantNumeric: 'tabular-nums',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {data.count > 99 ? '99+' : data.count}
    </span>
  )
}

/** Collapsed-rail dot: blue if any risk badge present, neutral otherwise. */
function CollapsedDot({ risk }: { risk: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 6,
        right: 12,
        width: 7,
        height: 7,
        borderRadius: 'var(--r-pill)',
        background: risk ? 'var(--info-500)' : 'var(--text-mute)',
      }}
    />
  )
}

function navLinkStyle(active: boolean, collapsed: boolean): CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 40,
    padding: collapsed ? 0 : '0 12px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 'var(--r-sm)',
    color: active ? 'var(--text)' : 'var(--text-soft)',
    background: active
      ? 'color-mix(in srgb, var(--brand-500) 10%, transparent)'
      : 'transparent',
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    transition: 'background var(--dur-fast), color var(--dur-fast)',
  }
}

function NavLink({
  item,
  collapsed,
  active,
  badges,
}: {
  item: NavItem
  collapsed: boolean
  active: boolean
  badges: BadgeData[]
}) {
  const Icon = item.icon
  const hasRisk = badges.some((b) => b.risk)
  const tooltipSuffix = badges
    .map((b) => `${b.count} ${BADGE_NOUN[b.badge]}`)
    .join(' · ')
  const link = (
    <Link
      to={item.to}
      className="it-nav-link"
      aria-current={active ? 'page' : undefined}
      style={navLinkStyle(active, collapsed)}
    >
      {active && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: collapsed ? 2 : 0,
            top: 7,
            bottom: 7,
            width: 3,
            borderRadius: 'var(--r-pill)',
            background: 'var(--brand-500)',
          }}
        />
      )}
      <Icon
        size={18}
        aria-hidden
        color={active ? 'var(--brand-500)' : 'var(--text-mute)'}
        style={{ flexShrink: 0 }}
      />
      {!collapsed && <span style={{ minWidth: 0 }}>{item.label}</span>}
      {!collapsed && badges.length > 0 && (
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
          {badges.map((b) => (
            <Badge key={b.badge} data={b} />
          ))}
        </span>
      )}
      {collapsed && badges.length > 0 && <CollapsedDot risk={hasRisk} />}
    </Link>
  )

  if (!collapsed) return link
  return (
    <Tooltip.Root>
      <Tooltip.Trigger render={link} />
      <Tooltip.Portal>
        <Tooltip.Positioner side="right" sideOffset={10}>
          <Tooltip.Popup style={TOOLTIP_STYLE}>
            {item.label}
            {tooltipSuffix ? ` · ${tooltipSuffix}` : ''}
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

/**
 * A sub-item is active when every search param it sets matches the current
 * URL — or, for the route's default view, when the param is absent (the page
 * omits defaults from the URL).
 */
function isSubActive(sub: NavSubItem, search: Record<string, unknown>): boolean {
  return Object.entries(sub.search).every(([key, value]) => {
    const current = search[key]
    if (current === undefined || current === '') return sub.isDefault === true
    return String(current) === value
  })
}

/**
 * Indented sub-tabs under a nav item (expanded rail only). Collapsed by
 * default; expands while the parent is active. The 0fr→1fr grid-row trick
 * animates height without measuring content.
 */
function SubNav({
  item,
  parentActive,
  search,
  reducedMotion,
}: {
  item: NavItem
  parentActive: boolean
  search: Record<string, unknown>
  reducedMotion: boolean
}) {
  return (
    <div
      aria-hidden={parentActive ? undefined : true}
      style={{
        display: 'grid',
        gridTemplateRows: parentActive ? '1fr' : '0fr',
        transition: reducedMotion
          ? undefined
          : 'grid-template-rows var(--dur-base) var(--ease-out)',
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0, visibility: parentActive ? 'visible' : 'hidden' }}>
        <div
          role="group"
          aria-label={`${item.label} views`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            margin: '2px 0 4px 20px',
            paddingLeft: 10,
            borderLeft: '1px solid var(--border)',
          }}
        >
      {(item.children ?? []).map((sub) => {
        const active = parentActive && isSubActive(sub, search)
        return (
          <Link
            key={sub.key}
            to={item.to}
            search={sub.search as Record<string, never>}
            className="it-nav-link"
            aria-current={active ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 24,
              padding: '0 10px',
              borderRadius: 'var(--r-sm)',
              fontSize: 11.5,
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--text)' : 'var(--text-mute)',
              background: active
                ? 'color-mix(in srgb, var(--brand-500) 8%, transparent)'
                : 'transparent',
              transition: 'background var(--dur-fast), color var(--dur-fast)',
            }}
          >
            {sub.label}
          </Link>
        )
      })}
        </div>
      </div>
    </div>
  )
}

function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 10,
        padding: collapsed ? 0 : '0 16px',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      <img
        src="/assets/logos/logo_mark.png"
        alt={collapsed ? 'IntegraTrace' : ''}
        aria-hidden={collapsed ? undefined : true}
        width={26}
        height={26}
        style={{ flexShrink: 0, objectFit: 'contain', display: 'block' }}
      />
      {!collapsed && (
        <span
          style={{
            fontFamily: 'var(--ff-display)',
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: 'var(--tracking-snug)',
            color: 'var(--text)',
          }}
        >
          IntegraTrace
        </span>
      )}
    </div>
  )
}

export interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Overlay mode (<1024px): rendered fixed, slides in from the left. */
  overlay: boolean
  /** Overlay open state — ignored when not in overlay mode. */
  open: boolean
  reducedMotion: boolean
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  overlay,
  open,
  reducedMotion,
}: SidebarProps) {
  const { location } = useRouterState()
  const badgeCounts = useBadgeCounts()
  const isCollapsed = overlay ? false : collapsed

  const overlayStyle: CSSProperties = overlay
    ? {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 50,
        width: RAIL_WIDTH,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: reducedMotion ? 'none' : 'transform var(--dur-base) var(--ease-out)',
        boxShadow: open ? 'var(--sh-4)' : 'none',
      }
    : { gridArea: 'rail', width: isCollapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH }

  return (
    <Tooltip.Provider>
      <aside
        aria-label="Primary"
        aria-hidden={overlay && !open ? true : undefined}
        style={{
          background: 'var(--bg-elev)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition:
            overlay || reducedMotion ? undefined : 'width var(--dur-base) var(--ease-out)',
          ...overlayStyle,
        }}
      >
        <Brand collapsed={isCollapsed} />
        <nav
          aria-label="Main navigation"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {NAV_SECTIONS.map((section, si) => (
            <Fragment key={section.label}>
              {isCollapsed ? (
                si > 0 && (
                  <hr
                    aria-hidden
                    style={{
                      border: 0,
                      borderTop: '1px solid var(--border)',
                      margin: '6px 8px',
                    }}
                  />
                )
              ) : (
                <div
                  style={{
                    padding: `${si === 0 ? 8 : 18}px 12px 4px`,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--text-faint)',
                  }}
                >
                  {section.label}
                </div>
              )}
              {section.items.map((item) => {
                const active = isNavActive(item, location.pathname)
                return (
                  <Fragment key={item.to}>
                    <NavLink
                      item={item}
                      collapsed={isCollapsed}
                      active={active}
                      badges={visibleBadges(item, badgeCounts)}
                    />
                    {!isCollapsed && item.children && (
                      <SubNav
                        item={item}
                        parentActive={active}
                        search={location.search as Record<string, unknown>}
                        reducedMotion={reducedMotion}
                      />
                    )}
                  </Fragment>
                )
              })}
            </Fragment>
          ))}
        </nav>
        {!overlay && (
          <button
            type="button"
            className="it-icon-btn it-shell-btn"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 36,
              margin: 8,
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-mute)',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {collapsed ? (
              <ChevronsRight size={14} aria-hidden />
            ) : (
              <ChevronsLeft size={14} aria-hidden />
            )}
            {!collapsed && <span>Collapse</span>}
          </button>
        )}
      </aside>
    </Tooltip.Provider>
  )
}
